import React from 'react'
import PropTypes from 'prop-types'
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import { Currency } from 'react-intl-number-format'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'

import { theme } from '../../theme'
import { getBankTransferDetailsFromOrder } from '../../utils/bankTransfer'
import { hasShortDisplayVariant, shortenOrderNumber } from '../../utils/orderNumber'
import { copyTextToClipboard } from '../../utils/clipboard'
import { downloadOrderProforma } from '../../utils/orderDocument'
import { getAvailabilityBreakdown, hasOnOrderQuantity } from '../../utils/itemPresentation'
import BankTransferDetailsCard from './BankTransferDetailsCard'
import BankTransferProofUploader from './BankTransferProofUploader'

const paymentLabels = {
  awaiting_payment: 'În așteptarea plății',
  paid: 'Plătită',
  payment_failed: 'Plata nu a fost confirmată',
  refunded: 'Rambursată'
}

const fulfillmentLabels = {
  new: 'Nouă',
  supplier_waiting: 'În așteptare furnizor',
  processing: 'În procesare',
  out_for_delivery: 'În livrare',
  completed: 'Finalizată',
  cancelled: 'Anulată'
}

const paymentChipColor = {
  awaiting_payment: 'warning',
  paid: 'success',
  payment_failed: 'error',
  refunded: 'default'
}

const fulfillmentChipColor = {
  new: 'default',
  supplier_waiting: 'warning',
  processing: 'info',
  out_for_delivery: 'info',
  completed: 'success',
  cancelled: 'error'
}

const bankTransferLabels = {
  awaiting_proof: 'Asteapta dovada',
  proof_uploaded: 'Dovada trimisa',
  confirmed: 'Plata confirmata',
  rejected: 'Dovada respinsa',
  not_applicable: 'N/A'
}

const bankTransferChipColor = {
  awaiting_proof: 'warning',
  proof_uploaded: 'info',
  confirmed: 'success',
  rejected: 'error',
  not_applicable: 'default'
}

const getPrimaryActionSx = () => ({
  backgroundColor: `${theme.palette.accent.main} !important`,
  color: '#FFFFFF !important',
  border: `1px solid ${theme.palette.accent.main} !important`,
  borderRadius: '12px',
  '&:hover': {
    backgroundColor: `${theme.palette.accent.light} !important`,
    borderColor: `${theme.palette.accent.light} !important`
  }
})

const getSecondaryActionSx = () => ({
  backgroundColor: '#F5F5F5 !important',
  color: `${theme.palette.fontColor.dark} !important`,
  border: `1px solid ${theme.palette.borderColor.main} !important`,
  borderRadius: '12px',
  boxShadow: 'none !important',
  '&:hover': {
    backgroundColor: '#EBEBEB !important',
    borderColor: `${theme.palette.fontColor.main} !important`
  }
})

const getFinalInvoiceUrl = (order) => (
  order?.final_invoice?.download_url || order?.final_invoice_download_url || ''
)

const getStripePaymentDocumentUrl = (order) => (
  order?.stripe_receipt_url || order?.stripe_invoice_pdf || order?.stripe_invoice_url || ''
)

const canShowFinalInvoice = (order) => Boolean(order?.customer_invoice_available && getFinalInvoiceUrl(order))

const canShowStripePaymentDocument = (order) => Boolean(
  order?.customer_invoice_available &&
  getStripePaymentDocumentUrl(order)
)

const isPaidBankTransferWithoutInvoice = (order) => (
  order?.payment_method === 'bank_transfer' &&
  order?.payment_status === 'paid' &&
  !canShowFinalInvoice(order)
)

const isPaidCardWithoutFinalInvoice = (order) => (
  order?.payment_method !== 'bank_transfer' &&
  order?.payment_status === 'paid' &&
  !canShowFinalInvoice(order)
)

const AvailabilitySplit = ({ item, compact = false }) => {
  const availability = getAvailabilityBreakdown(item, item?.quantity)
  const chipSx = {
    height: compact ? 19 : 22,
    '& .MuiChip-label': {
      px: compact ? 0.8 : 1,
      fontSize: compact ? '9px' : '10px',
      fontWeight: 800
    }
  }

  return (
    <Stack direction='row' spacing={0.75} flexWrap='wrap' useFlexGap sx={{ mt: 0.5 }}>
      {availability.inStockQuantity > 0 && (
        <Chip
          size='small'
          label={`In stoc: ${availability.inStockQuantity}`}
          sx={{
            ...chipSx,
            color: '#008000',
            backgroundColor: 'rgba(0, 128, 0, 0.11)'
          }}
        />
      )}
      {availability.onOrderQuantity > 0 && (
        <Chip
          size='small'
          label={`La comanda: ${availability.onOrderQuantity}`}
          sx={{
            ...chipSx,
            color: theme.palette.accent.main,
            backgroundColor: 'rgba(226, 6, 19, 0.08)',
            border: `1px solid ${theme.palette.accent.main}`
          }}
        />
      )}
      {availability.quantity === 0 && item?.stock_label && (
        <Typography variant='caption' sx={{ color: theme.palette.fontColor.medium }}>
          {item.stock_label}
        </Typography>
      )}
    </Stack>
  )
}

AvailabilitySplit.propTypes = {
  item: PropTypes.object,
  compact: PropTypes.bool
}

const getDocumentActions = (order) => {
  const actions = []

  if ((order?.payment_status === 'awaiting_payment' || order?.payment_status === 'payment_failed') && order?.stripe_checkout_url) {
    actions.push({
      key: 'resume-payment',
      label: order.payment_status === 'payment_failed' ? 'Reîncearcă plata' : 'Continuă plata',
      href: order.stripe_checkout_url,
      variant: 'contained',
      sx: getPrimaryActionSx()
    })
  }

  if (canShowFinalInvoice(order)) {
    actions.push({
      key: 'final-invoice',
      label: 'Descarcă factura',
      href: getFinalInvoiceUrl(order),
      variant: 'contained',
      sx: getPrimaryActionSx()
    })
    actions.push({
      key: 'proforma',
      label: 'Descarcă proforma',
      onClick: () => downloadOrderProforma(order),
      variant: 'contained',
      sx: getSecondaryActionSx()
    })
  } else {
    actions.push({
      key: 'proforma',
      label: 'Descarcă proforma',
      onClick: () => downloadOrderProforma(order),
      variant: 'contained',
      sx: getPrimaryActionSx()
    })
  }

  if (canShowStripePaymentDocument(order)) {
    actions.push({
      key: 'stripe-payment-proof',
      label: 'Dovada plata Stripe',
      href: getStripePaymentDocumentUrl(order),
      variant: 'contained',
      sx: getSecondaryActionSx()
    })
  }

  return actions
}

const OrderSummaryCard = ({ order, onOrderUpdated }) => {
  if (!order) {
    return null
  }

  const documentActions = getDocumentActions(order)
  const bankTransferDetails = getBankTransferDetailsFromOrder(order)
  const displayOrderNumber = shortenOrderNumber(order.order_number)
  const onOrderItems = (order.items || []).filter(item => hasOnOrderQuantity(item, item.quantity))

  return (
    <Box sx={{ border: '1px solid', borderColor: theme.palette.borderColor.main, borderRadius: '16px', p: 3, backgroundColor: theme.palette.primary.main }}>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent='space-between'
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={1}
        >
          <Box>
            <Stack direction='row' spacing={0.75} alignItems='center'>
              <Typography variant='h6' sx={{ fontWeight: 700 }} title={order.order_number}>
                {displayOrderNumber}
              </Typography>
              <Tooltip title='Copiaza numarul comenzii'>
                <IconButton
                  size='small'
                  aria-label='Copiaza numarul comenzii'
                  onClick={() => copyTextToClipboard(displayOrderNumber)}
                  sx={{
                    width: 28,
                    height: 28,
                    color: theme.palette.fontColor.medium,
                    '&:hover': { color: theme.palette.accent.main }
                  }}
                >
                  <ContentCopyOutlinedIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
            </Stack>
            {hasShortDisplayVariant(order.order_number) && (
              <Typography variant='caption' sx={{ display: 'block', color: theme.palette.fontColor.medium, lineHeight: 1.6 }}>
                Referinta completa: {order.order_number}
              </Typography>
            )}
            <Typography variant='body2' sx={{ color: theme.palette.fontColor.medium }}>
              Plasată la {order.created_at ? new Date(order.created_at).toLocaleString('ro-RO') : '-'}
            </Typography>
          </Box>
          <Typography component='div' sx={{ fontSize: '26px', fontWeight: 700, color: theme.palette.accent.main }}>
            <Currency locale='ro-RO' currency='RON'>{order.total_price}</Currency>
          </Typography>
        </Stack>

        <Stack direction='row' spacing={1} sx={{ flexWrap: 'wrap' }}>
          <Chip
            size='small'
            label={paymentLabels[order.payment_status] || order.payment_status}
            color={paymentChipColor[order.payment_status] || 'default'}
          />
          <Chip
            size='small'
            label={fulfillmentLabels[order.fulfillment_status] || order.fulfillment_status}
            color={fulfillmentChipColor[order.fulfillment_status] || 'default'}
          />
          {order.payment_method === 'bank_transfer' && (
            <Chip
              size='small'
              label={bankTransferLabels[order.bank_transfer_status] || order.bank_transfer_status}
              color={bankTransferChipColor[order.bank_transfer_status] || 'default'}
            />
          )}
        </Stack>

        {onOrderItems.length > 0 && (
          <Alert severity='warning' sx={{ '& .MuiAlert-message': { fontSize: '12px', lineHeight: 1.75 } }}>
            Comanda poate fi platita integral. Produsele marcate „La comanda” nu sunt complet pe stoc si pot avea termen de livrare mai mare.
          </Alert>
        )}

        <Divider />

        <Stack spacing={1.25}>
          {order.items?.map(item => (
            <Box key={`${order._id}-${item.item_id}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start' }}>
              <Box>
                <Typography variant='body2' sx={{ fontWeight: 600 }}>
                  {item.quantity} x {item.name}
                </Typography>
                <AvailabilitySplit item={item} />
              </Box>
              <Typography component='div' variant='body2' sx={{ whiteSpace: 'nowrap' }}>
                <Currency locale='ro-RO' currency='RON'>{item.total_price}</Currency>
              </Typography>
            </Box>
          ))}
        </Stack>

        <Divider />

        <Box>
          <Typography sx={{ fontSize: '15px', fontWeight: 700, marginBottom: '10px' }}>
            Documente și acțiuni
          </Typography>
          {isPaidBankTransferWithoutInvoice(order) && (
            <Typography variant='body2' sx={{ color: theme.palette.fontColor.medium, marginBottom: '10px' }}>
              Proforma ramane disponibila in cont. Pentru ordin de plata, factura finala este emisa separat de echipa Primusvest dupa confirmarea interna.
            </Typography>
          )}
          {isPaidCardWithoutFinalInvoice(order) && (
            <Typography variant='body2' sx={{ color: theme.palette.fontColor.medium, marginBottom: '10px' }}>
              Plata este confirmata. Documentul Stripe ramane dovada platii, iar factura finala Primusvest va deveni disponibila dupa procesare.
            </Typography>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ flexWrap: 'wrap' }}>
            {documentActions.map(action => (
              <Button
                key={action.key}
                component={action.href ? 'a' : 'button'}
                href={action.href}
                target={action.href ? '_blank' : undefined}
                rel={action.href ? 'noreferrer' : undefined}
                size='small'
                variant={action.variant}
                onClick={action.onClick}
                sx={action.sx}
              >
                {action.label}
              </Button>
            ))}
          </Stack>
          {order.payment_method === 'bank_transfer' && (
            <Stack spacing={1} sx={{ marginTop: '10px' }}>
              <BankTransferDetailsCard
                details={bankTransferDetails}
                helperText='Foloseste exact contul afisat pentru transfer, descarca proforma si apoi incarca dovada platii. Referinta platii trebuie sa fie numarul comenzii.'
              />
              <BankTransferProofUploader order={order} onUploaded={onOrderUpdated} />
            </Stack>
          )}
        </Box>

        <Divider />

        <Stack spacing={0.75}>
          <Typography variant='body2'><strong>Facturare:</strong> {order.billing_snapshot?.full_address || 'Adresa nu este disponibilă.'}</Typography>
          <Typography variant='body2'><strong>Livrare:</strong> {order.delivery_snapshot?.full_address || order.billing_snapshot?.full_address || 'Adresa nu este disponibilă.'}</Typography>
        </Stack>

        <Divider />

        <Stack spacing={0.75}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Typography variant='body2'>Subtotal</Typography>
            <Typography component='div' variant='body2'><Currency locale='ro-RO' currency='RON'>{order.subtotal_price}</Currency></Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Typography variant='body2'>Transport</Typography>
            <Typography component='div' variant='body2'><Currency locale='ro-RO' currency='RON'>{order.shipping_price}</Currency></Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Typography sx={{ fontWeight: 700 }}>Total</Typography>
            <Typography component='div' sx={{ fontWeight: 700 }}><Currency locale='ro-RO' currency='RON'>{order.total_price}</Currency></Typography>
          </Box>
        </Stack>

      </Stack>
    </Box>
  )
}

OrderSummaryCard.propTypes = {
  order: PropTypes.object,
  onOrderUpdated: PropTypes.func
}

export default OrderSummaryCard
